import {
  SessionManager,
  applyMissionText,
  assessPlausibility,
  evidenceOf,
  missionText,
  type GitEvidence,
  type GradeResult,
  type Locale,
  type GitState,
  type Mission,
  type MissionText,
  type Session,
} from '@engineer-os/engine';
import { BrowserDriver, TarballSeeder, bootVm, fetchManifest, type Vm, type VmProgress } from '@engineer-os/vm';
import { saltedMission } from '@engineer-os/engine';
import { seedSalts } from '../lib/verification';
import { WorkKeeper, type Resumed } from './keeper';
import { levelProgress, loadProgress } from '../progress';
import { getLocale, levelsLocalised } from '../lib/locale';
// Two of the throws in this file are read by a learner rather than by whoever
// wrote it, and those two come from the catalogue. `getStrings()` and not the
// hook: this module is the server, and there is no component here to re-render
// (web/src/lib/strings/index.ts). Every other message below names a URL, a
// schema or an id, and stays English where the person who must act on it
// works.
import { getStrings } from '../lib/strings';
// The bundler emits this: 2.1 MB copied into assets/ under a content-hashed
// name, because the wasm is pinned by package-lock and versioned with the code
// that imports it, not with the image (docs/contracts.md, "The asset split").
import v86WasmUrl from 'v86/build/v86.wasm?url';

/**
 * The server, in the tab.
 *
 * server/src/index.ts holds three things behind its eleven routes: the
 * missions it loaded from disk, a workspace driver, and a SessionManager
 * over both. This holds the same three, with the missions arriving as one
 * JSON file instead of a directory walk and the driver reaching into a Linux
 * emulated in this page. Everything above it — sessions, grading, the git
 * graph, the file API — is the engine code the server runs, unchanged, which
 * is what lets api.ts answer the same calls with no network.
 *
 * One VM boots per visit and never reboots: it costs thirteen megabytes and
 * a second and a half of restore, and a level change is a tar extraction
 * inside the guest instead. That single VM has a single /workspace, which is
 * why exactly one session is alive at a time (see `start`).
 */

/* --- Where the pieces are served from ------------------------------------ */

/**
 * The VM's assets are cross-origin in production (R2, because Workers Static
 * Assets answers a range request with the whole 192 MB file), while
 * missions.json and v86.wasm ship beside the app. Both are overridable so a
 * developer can point the tab at a locally built image without a rebuild.
 */
function envString(name: string): string | undefined {
  const value = (import.meta.env as Record<string, unknown>)[name];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Every `VmAsset.path` resolves against this. `VmBootOptions.assetBase` is
 * specified as an *absolute* URL ending in a slash — v86 is handed these
 * strings and resolves nothing itself — so a configured value, which is
 * normally the R2 custom domain but in development is often just `/vm/`, is
 * resolved against the page before it goes anywhere near the VM.
 */
const ASSET_BASE = new URL(
  (envString('VITE_VM_ASSETS') ?? `${import.meta.env.BASE_URL}vm/`).replace(/\/*$/, '/'),
  window.location.href,
).href;
const MANIFEST_URL = `${ASSET_BASE}manifest.json`;
const WASM_URL = envString('VITE_V86_WASM') ?? v86WasmUrl;
const MISSIONS_URL = `${import.meta.env.BASE_URL}missions.json`;

/**
 * The paid half of the course, which is not an asset and has no URL under
 * BASE_URL: it is held by the Worker and handed over only to a session that
 * has bought it (worker/entitlement.ts).
 */
const PAID_MISSIONS_URL = '/api/missions';
/** The efficiency benchmark's tasks, for a signed-in tab (worker/bench.ts). */
const BENCH_MISSIONS_URL = '/api/bench/missions';

/**
 * Bumped with the shape of missions.json. Schema 2 is the split file: the
 * free levels in full and the paid ones as locked stubs, one array in course
 * order. Which levels those are is a fact about how many chapters have
 * shipped, so it is not written down here.
 */
const MISSIONS_SCHEMA = 2;

/**
 * The levels' words, in a language that is not English.
 *
 * One file per language beside the catalogue, rather than both languages in
 * one: an English reader downloads no Arabic, and an Arabic reader downloads
 * one extra file the first time they ask for it. `missions.ar.json` is a
 * lookup keyed by mission id (scripts/build-missions.mjs, `localeText`) and it
 * holds strings only — no check, no `with:`, no seed. There is one grader per
 * level and it is not translated.
 */
const localeMissionsUrl = (locale: Locale) => `${import.meta.env.BASE_URL}missions.${locale}.json`;

/** The paid half's words, behind the same entitlement as the paid half itself. */
const localePaidUrl = (locale: Locale) => `/api/missions?lang=${encodeURIComponent(locale)}`;

/* --- The two files fetched before anything boots ------------------------- */

/**
 * Read one JSON file from the app's own origin, giving each attempt a time
 * limit and the network a second and third chance.
 *
 * Without the limit a connection that went quiet mid-response left the boot
 * screen waiting for ever (measured 2026-10-01 for the VM's own downloads;
 * this is the same `fetch`). An answer from the server is not retried.
 */
async function fetchJson(url: string, attempts = 3, attemptMs = 45_000): Promise<unknown> {
  for (let n = 1; ; n++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), attemptMs);
    try {
      const res = await fetch(url, { credentials: 'omit', signal: controller.signal });
      if (!res.ok) {
        const err = new Error(`${url}: ${res.status} ${res.statusText}`);
        if (res.status < 500 && res.status !== 408 && res.status !== 429) throw Object.assign(err, { final: true });
        throw err;
      }
      return (await res.json()) as unknown;
    } catch (err) {
      if ((err as { final?: boolean }).final || n >= attempts) {
        throw controller.signal.aborted ? new Error(`${url} did not arrive within ${attemptMs / 1000} s`) : err;
      }
    } finally {
      clearTimeout(timer);
    }
    await new Promise((resolve) => setTimeout(resolve, 1_000 * n));
  }
}

/*
 * The manifest is read by @engineer-os/vm's own `fetchManifest`, not by a
 * second reader here. It checks every asset's path, byte count and SHA-256
 * as well as the schema, and throws a `VmError` whose `code` is 'manifest',
 * which is the code the boot screen already has a sentence for. Two readers
 * of one contract is exactly how a build ships a manifest one of them
 * accepts and the other refuses.
 */

/**
/**
 * The locked stubs, exactly as the catalogue was served, kept aside before
 * anything is folded over them.
 *
 * `unlockPaid` replaces a stub with the real level in place, and for most of
 * this file's life that was a one-way door: the only way back to a locked
 * catalogue was a reload. Signing out is the way back that has to work
 * without one. An account is the only thing that can hold the course
 * (worker/invite.ts refuses a code to a signed-out browser), so the moment
 * there is no account there is no entitlement, and a tab still holding the
 * paid half is a tab showing a course it has no claim on: the offer block
 * disappears, because "nothing is locked" is how it decides there is nothing
 * to sell (lib/entitlement.ts), and every paid row on the map reads as merely
 * unearned rather than unbought.
 *
 * The original objects rather than copies, so that putting one back restores
 * the identity everything else in this module compares against, and so that
 * whatever `applyLocale` wrote onto it at startup is still on it.
 */
const stubs = new Map<string, Mission>();

/**
 * missions.json is `ClientMission[]`, which is a `Mission` with the authoring
 * fields (seed, solution, wrong solutions) removed at build time. The engine
 * declares those optional precisely so this cast is honest: `gradeMission`
 * and `publicMission` take what comes back unchanged.
 */
async function loadMissionCatalog(): Promise<Map<string, Mission>> {
  let raw: unknown;
  try {
    raw = await fetchJson(MISSIONS_URL);
  } catch (err) {
    throw new Error(`could not read the level list (${(err as Error).message})`);
  }
  const file = raw as { schema?: unknown; missions?: unknown } | null;
  if (!file || typeof file !== 'object' || file.schema !== MISSIONS_SCHEMA) {
    throw new Error(`${MISSIONS_URL} is not a schema ${MISSIONS_SCHEMA} mission file`);
  }
  // A catalogue that parsed and holds nothing is the one failure here a
  // learner sees as a sentence rather than as a detail line, so it is theirs.
  if (!Array.isArray(file.missions) || file.missions.length === 0) throw new Error(getStrings().boot.runtime.noLevels);

  const missions = (file.missions as Mission[]).slice();
  for (const m of missions) {
    // A locked stub is a level this browser was not given (engine: `Mission.
    // locked`), so it has no objectives and is not expected to. Everything
    // else must be a mission.
    if (typeof m?.id !== 'string' || !Number.isInteger(m?.level)) {
      throw new Error(`${MISSIONS_URL} contains a level that is not a mission`);
    }
    // `requires` is checked here rather than left to the gate, because the
    // gate's honest reading of an absent one is "this level starts a path",
    // and a catalogue where every level said that would be a catalogue with
    // no gate at all. A stub carries it too — being unbought is not a reason
    // to stop knowing what comes first — so there is no level this may skip.
    if (!Array.isArray(m.requires)) {
      throw new Error(`${MISSIONS_URL} contains a level with no prerequisites recorded`);
    }
    if (m.locked !== true && !Array.isArray(m.objectives)) {
      throw new Error(`${MISSIONS_URL} contains a level that is not a mission`);
    }
  }
  // The same order and the same rule as server/src/missions.ts, now read per
  // track: `level` is a position on *a* path rather than on the only one, so
  // two tracks may each hold a level 0 without the map becoming ambiguous.
  // Two levels in one position on the *same* track still would be, and the
  // check is a lookup rather than a walk over neighbours because it no longer
  // depends on the list arriving in any particular order.
  const seen = new Map<string, string>();
  for (const m of missions) {
    const track = m.track ?? 'general';
    const other = seen.get(`${track}#${m.level}`);
    if (other !== undefined) {
      throw new Error(`levels "${other}" and "${m.id}" both claim position ${m.level} on track "${track}"`);
    }
    seen.set(`${track}#${m.level}`, m.id);
  }
  // Course order, which is what everything downstream reads this list as: the
  // general track first because it is the one every learner walks, then each
  // specialization, each in its own numbering.
  missions.sort((a, b) => {
    const at = a.track ?? 'general';
    const bt = b.track ?? 'general';
    if (at === bt) return a.level - b.level;
    if (at === 'general') return -1;
    if (bt === 'general') return 1;
    return at.localeCompare(bt);
  });
  // Kept aside before anything is handed out, so that the paid half can be
  // taken back when the account that held it goes away. See `stubs`.
  for (const m of missions) if (m.locked) stubs.set(m.id, m);
  return new Map(missions.map((m) => [m.id, m]));
}

/* --- Boot progress -------------------------------------------------------- */

export type { VmProgress } from '@engineer-os/vm';

const watchers = new Set<(progress: VmProgress) => void>();
let lastProgress: VmProgress | null = null;

/**
 * Watch the boot; returns the unsubscribe.
 *
 * A new watcher is handed the last progress it missed, because the boot
 * screen usually mounts after the boot has started and would otherwise show
 * nothing until the next phase — which, during the download, is seconds.
 */
export function onVmProgress(cb: (progress: VmProgress) => void): () => void {
  watchers.add(cb);
  if (lastProgress) cb(lastProgress);
  return () => {
    watchers.delete(cb);
  };
}

function report(progress: VmProgress): void {
  lastProgress = progress;
  for (const cb of watchers) cb(progress);
}

/* --- Opening a level ------------------------------------------------------ */

/**
 * Which part of opening a level is happening now.
 *
 * The boot screen narrates the machine starting because a first visit is
 * thirteen megabytes and nobody expects it. Opening a level is the same
 * argument at a smaller scale and was not making it: the wait is seconds of
 * guest work on a single serial channel — the sweep, the extraction, a boot
 * script, the first read of the repository — behind one motionless sentence,
 * and a learner reads a motionless sentence as a hang rather than as work.
 *
 * `starting` happens only on the levels that have a `boot.sh`, which is why
 * the screen takes the mission's own word for whether to draw that step
 * rather than showing a step that will never light up.
 */
export type LevelPhase = 'clearing' | 'unpacking' | 'starting' | 'reading';

/** A phase, the level it belongs to, and every phase that level will go through. */
export interface LevelProgress {
  missionId: string;
  phase: LevelPhase;
  /**
   * The whole plan, in order, so the screen draws the steps it is told
   * rather than working them out.
   *
   * It is decided here because the mission is here: whether a level has a
   * boot step is a fact about the level, and `PublicMission` — which is all
   * the screen has — deliberately does not carry the script. Sending the
   * plan rather than the script keeps it that way.
   */
  steps: LevelPhase[];
}

const levelWatchers = new Set<(progress: LevelProgress) => void>();
let lastLevelProgress: LevelProgress | null = null;
let openingSteps: LevelPhase[] = [];

/** Which steps opening this level will take. The boot step only where there is one. */
function planLevel(mission: Mission | undefined): LevelPhase[] {
  const boot = mission?.boot;
  return boot !== undefined && boot.trim() !== ''
    ? ['clearing', 'unpacking', 'starting', 'reading']
    : ['clearing', 'unpacking', 'reading'];
}

/**
 * Watch a level open; returns the unsubscribe.
 *
 * The last phase is replayed, as the boot screen's is, because the open
 * begins inside `useSession`'s effect and the screen subscribes in its own —
 * and it carries the mission id precisely so that a replayed phase from the
 * level before this one can be ignored rather than drawn.
 */
export function onLevelPhase(cb: (progress: LevelProgress) => void): () => void {
  levelWatchers.add(cb);
  if (lastLevelProgress) cb(lastLevelProgress);
  return () => {
    levelWatchers.delete(cb);
  };
}

function reportLevel(missionId: string, phase: LevelPhase): void {
  const progress: LevelProgress = { missionId, phase, steps: openingSteps };
  lastLevelProgress = progress;
  for (const cb of [...levelWatchers]) cb(progress);
}

/* --- The runtime ---------------------------------------------------------- */

/**
 * A level that has just opened, and the repository as it was at that moment.
 *
 * The state is here rather than left to the caller because both readers want
 * the same read: the plausibility evidence below, and the bundle `api.ts`
 * hands the screen. It was two reads of the same unchanged repository, which
 * on this machine is a git graph script apiece — 0.6 to 2.5 s each, measured
 * in the guest — with nothing able to happen in between.
 *
 * It is null only when that read failed, which is not allowed to stop a level
 * from opening: there is then no evidence to judge, and the caller that needs
 * a state for the screen asks for its own and fails in its own way.
 */
export interface OpenedLevel {
  session: Session;
  state: GitState | null;
  /** Whether the learner's unfinished work came back, or could not because the level changed (keeper.ts). */
  resumed: Resumed;
}

export interface Runtime {
  readonly vm: Vm;
  readonly missions: Map<string, Mission>;
  readonly sessions: SessionManager;
  /** Start a level: the previous one's workspace is gone by the time this returns. */
  start(missionId: string): Promise<OpenedLevel>;
  /** Grade the session and, when it passes, say whether the pass looks earned. */
  check(session: Session): Promise<GradeResult>;
  /** Drop this level's kept work: the learner asked to start it again. */
  forgetWork(missionId: string): Promise<void>;
}

/**
 * What the runtime remembers about a level from the moment it opened: when,
 * and what the repository looked like. `sessions.grade` is the server's
 * grader, pure and clockless; this is the context it never had, kept beside
 * the session rather than in it because the engine has no `performance`.
 */
interface Opening {
  /** performance.now() at open. Monotonic, so a system clock set back cannot make a level look faster. */
  t0: number;
  /** The seeded repository's fingerprint, or null if it could not be read. */
  before: GitEvidence | null;
  /** Commands and saves made before a reload, when this open restored kept work (keeper.ts). */
  carried: { commands: number; saves: number };
}

/**
 * When a level was first opened in this tab, on the wall clock, kept in
 * sessionStorage beside the session id that useSession keeps there.
 *
 * `Opening.t0` dies with the page, and a reload is how a learner recovers a
 * stalled emulator. Without this, a learner who read and explored level 10
 * for ten minutes, reloaded, and typed the two commands from memory would
 * be measured from the reload and flagged for knowing the answer. So the
 * elapsed time is the *larger* of the two clocks: in-page monotonic time,
 * which a system clock cannot shorten, and wall time since the first open,
 * which a reload cannot shorten. Neither can make a level look faster than
 * the other says it was; either can only make it look slower, and slower
 * is the direction that never flags anyone.
 *
 * The entry is forgotten when the level passes with nothing to flag, so the
 * next visit to that level starts its own clock. Until then it stays, which
 * is the point: a level cleared with a flag is still the level in progress.
 */
const openedKey = (missionId: string) => `engineeros.opened.${missionId}`;

function firstOpened(missionId: string): number {
  const wall = Date.now();
  try {
    const stored = Number(sessionStorage.getItem(openedKey(missionId)));
    if (Number.isFinite(stored) && stored > 0 && stored <= wall) return stored;
    sessionStorage.setItem(openedKey(missionId), String(wall));
  } catch {
    /* storage unavailable: the in-page clock is all there is */
  }
  return wall;
}

/**
 * A restored level's first open, carried in its kept work (keeper.ts): the
 * earlier of that and whatever this tab already holds, so a level closed
 * yesterday and finished today is measured from yesterday.
 */
function adoptOpened(missionId: string, at: number): number {
  const current = firstOpened(missionId);
  if (!(at > 0) || at >= current) return current;
  try {
    sessionStorage.setItem(openedKey(missionId), String(at));
  } catch {
    /* storage unavailable: `firstOpened` falls back to now, which only ever flags less */
  }
  return at;
}

function forgetOpened(missionId: string): void {
  try {
    sessionStorage.removeItem(openedKey(missionId));
  } catch {
    /* storage unavailable */
  }
}

class BrowserRuntime implements Runtime {
  private openings = new Map<string, Opening>();
  constructor(
    readonly vm: Vm,
    readonly missions: Map<string, Mission>,
    readonly sessions: SessionManager,
    private readonly keeper: WorkKeeper,
  ) {}

  async start(missionId: string): Promise<OpenedLevel> {
    // The last gate in front of a paid level, and the one that has to hold
    // even if the map and the router are both wrong. `SessionManager.create`
    // has no idea what a locked mission is, so this is the only place between
    // a stub and the grader. It checks the flag rather than the shape on
    // purpose: a stub happens to carry no `objectives` key and would throw
    // downstream, but an empty list would pass vacuously, and which of those
    // a stub has is not a property worth betting a paywall on.
    const mission = this.missions.get(missionId);
    if (mission?.locked) {
      // Play.tsx shows this one to the learner, as a toast and then as the
      // reason the level would not open, so it is written for them.
      throw new Error(getStrings().boot.runtime.paidLevel(missionId));
    }
    if (!(missionId in this.vm.manifest.levels)) {
      throw new Error(`level "${missionId}" was not baked into this image, so it has no workspace to open`);
    }
    // One VM, one /workspace: the previous level's session cannot still have
    // one. Destroying it *first* is what matters — `destroy` wipes the
    // workspace, so doing it afterwards would empty the level just seeded.
    openingSteps = planLevel(mission);
    reportLevel(missionId, 'clearing');
    // The level being left is copied out before its workspace is wiped, and
    // this level's kept work, if it can go back, is armed for the seeder.
    await this.keeper.flush();
    this.keeper.stop();
    if (mission) await this.keeper.prepare(mission);
    await this.sessions.destroyAll();
    this.openings.clear();
    // `unpacking` and `starting` are reported from inside the seeder, which
    // is the only thing that knows whether this level has a boot step.
    const session = await this.sessions.create(missionId);
    const { resumed, carried } = this.keeper.opened(missionId);
    // Where this attempt began is where it began before the reload, not the
    // restored HEAD: set before the first state read, which records it only
    // when it is unset.
    if (carried?.startHead) session.startHead = carried.startHead;
    // Taken once, here, and handed back for the bundle as well. A reset
    // rebuilds the same seed, so the fingerprint would not change; and the
    // clock deliberately keeps running through a reset (same session, same
    // `t0`) and through a reload (`firstOpened`), because a learner who
    // worked for ten minutes, reset, and then solved it in twelve seconds
    // from memory has plainly done the work. Reading the state is not allowed
    // to stop the level from opening: with no fingerprint there is simply no
    // evidence to judge.
    reportLevel(missionId, 'reading');
    let state: GitState | null = null;
    let before: GitEvidence | null = null;
    try {
      state = await this.sessions.state(session);
      before = evidenceOf(state);
    } catch {
      /* no evidence, no evidence-based flag */
    }
    // A restored level is judged against the repository it was seeded with,
    // and from the moment it first opened, not against the restored one and
    // from the reload — or honest work done before a reload would look like
    // an answer typed from memory after it.
    if (carried) before = carried.before;
    const openedAt = carried ? adoptOpened(missionId, carried.openedAt) : firstOpened(missionId);
    const counts = { commands: carried?.commands ?? 0, saves: carried?.saves ?? 0 };
    this.openings.set(session.id, { t0: performance.now(), before, carried: counts });
    this.keeper.watch(session, openedAt, before, carried);
    return { session, state, resumed };
  }

  forgetWork(missionId: string): Promise<void> {
    return this.keeper.forget(missionId);
  }

  /** Wall time since this level opened, the longer of the two clocks (see `firstOpened`). */
  private elapsedMs(session: Session): number {
    const opening = this.openings.get(session.id);
    if (!opening) return Number.POSITIVE_INFINITY;
    const inPage = performance.now() - opening.t0;
    const sinceFirstOpen = Date.now() - firstOpened(session.mission.id);
    return Math.max(inPage, sinceFirstOpen);
  }

  async check(session: Session): Promise<GradeResult> {
    const result = await this.sessions.grade(session);
    if (!result.passed) return result;

    const opening = this.openings.get(session.id);
    // A fresh read rather than the last graph refresh: the graph lags a
    // command by one round trip, and the grade was just taken from the
    // repository as it is now.
    let after: GitEvidence | null = null;
    try {
      after = evidenceOf(await this.sessions.state(session));
    } catch {
      /* as above */
    }
    const counted = (type: 'command' | 'save') =>
      session.telemetry.filter((e) => e.type === type).length + (opening?.carried[type === 'command' ? 'commands' : 'saves'] ?? 0);
    result.flags = assessPlausibility({
      mission: session.mission,
      elapsedMs: this.elapsedMs(session),
      commands: counted('command'),
      saves: counted('save'),
      evidence: opening?.before && after ? { before: opening.before, after } : null,
      // A level this learner has already verified is being replayed, and a
      // replay of a level you know is fast by nature. Never flagged.
      replay: levelProgress(loadProgress(), session.mission.id).verified,
    });
    // A clean pass ends the level's clock. A flagged one does not: the level
    // is still open until a replay looks plausible, and the time already
    // spent in it counts towards that replay, reload or no reload.
    if (result.flags.length === 0) {
      forgetOpened(session.mission.id);
      // And its kept work: the next open of a level already passed starts it fresh.
      await this.keeper.finish(session.mission.id);
    }
    return result;
  }
}

/**
 * One attempt at a time, and a failure does not poison the next one: a
 * learner who was offline for the first try must be able to retry without
 * reloading the page.
 */
function once<T>(run: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | null = null;
  return () => {
    pending ??= run().catch((err: unknown) => {
      pending = null;
      throw err;
    });
    return pending;
  };
}

/**
 * The levels, without the VM: the home screen needs them, and they are 100 KB.
 *
 * Every failure is tagged `catalogue` on the way out, because the boot screen
 * awaits this beside the machine and an uncoded throw from here used to be
 * counted as the guest never answering (worker/events.ts, `BOOT_FAILURES`).
 * The message is left alone: it is what the learner reads on the detail line.
 */
export const missionCatalog = once(() =>
  loadMissionCatalog().catch((err: unknown) => {
    throw Object.assign(err instanceof Error ? err : new Error(String(err)), { code: 'catalogue' });
  }),
);

/* --- The language the levels are in -------------------------------------- */

/**
 * Switching language must not cost a boot.
 *
 * The page is running a Linux that took a hundred megabytes and a restore to
 * get to, so a language switch cannot be a reload. It cannot be a rebuild of
 * the catalogue either: `bootRuntime` hands the same `Map` to the
 * `SessionManager`, so the grader, the seeder and the map all hold references
 * into it, and replacing entries would leave a session that is mid-level
 * pointing at the mission it started with — an objective would still fail in
 * English half a second after the interface turned Arabic.
 *
 * So the strings are written onto the mission objects everybody already
 * holds. `applyMissionText` does exactly that and nothing else: it touches no
 * `check`, no `with`, no seed. The two languages are the same fifty levels.
 *
 * The English is captured on the way past, the first time a mission is seen,
 * because after the first overlay the object no longer contains it. That
 * capture is what makes switching back free and lossless rather than a second
 * fetch.
 */
const english = new Map<string, MissionText>();

/** Per language, the overlay for the free half. Fetched once, on first use. */
const overlays = new Map<Locale, Map<string, MissionText>>();

/** The language the catalogue's strings are currently in. */
let applied: Locale = 'en';

/** Which call to `applyLocale` is the latest, so an overtaken one can stand down. */
let applying = 0;

/** Remember a mission's English before anything is written over it. */
function keepEnglish(m: Mission): void {
  if (!english.has(m.id)) english.set(m.id, missionText(m));
}

/**
 * A locale file's `text` map, validated only as far as it is used.
 *
 * Deliberately forgiving: a translation is decoration over a catalogue that
 * already works, and a malformed or missing one must leave the course in
 * English rather than break it. Every failure here ends in "no overlay", and
 * the caller falls back per string.
 */
function readOverlay(raw: unknown, where: string): Map<string, MissionText> {
  const file = raw as { schema?: unknown; text?: unknown } | null;
  if (!file || typeof file !== 'object' || file.schema !== MISSIONS_SCHEMA) {
    throw new Error(`${where} is not a schema ${MISSIONS_SCHEMA} translation file`);
  }
  const text = file.text;
  if (!text || typeof text !== 'object') throw new Error(`${where} carried no text`);
  return new Map(Object.entries(text as Record<string, MissionText>));
}

async function fetchOverlay(locale: Locale): Promise<Map<string, MissionText>> {
  const held = overlays.get(locale);
  if (held) return held;
  const url = localeMissionsUrl(locale);
  const text = readOverlay(await fetchJson(url), url);
  overlays.set(locale, text);
  return text;
}

/**
 * Write one mission's strings in the given language, in place.
 *
 * English is the source, so switching back means writing the captured
 * original rather than fetching anything. A mission the overlay does not
 * cover keeps whatever it has, which for a half-translated course is the
 * English — the right answer, and the reason nothing here throws.
 */
function localise(m: Mission, locale: Locale, text: Map<string, MissionText> | null): void {
  keepEnglish(m);
  if (locale === 'en') {
    const source = english.get(m.id);
    if (source) applyMissionText(m, source);
    return;
  }
  const translated = text?.get(m.id);
  if (!translated) {
    // Not translated yet. Put the English back, in case this mission is
    // currently wearing a third language's strings.
    const source = english.get(m.id);
    if (source) applyMissionText(m, source);
    return;
  }
  applyMissionText(m, translated);
}

/**
 * Put the whole catalogue into one language.
 *
 * Called once at startup with whatever language the page opened in, and again
 * on every switch. The caller re-reads the list afterwards — React has no way
 * to notice a mutation — exactly as it does after `unlockPaid`.
 *
 * A failure to fetch the overlay is not a failure: it is answered in English,
 * which is a worse experience and a working one. Only the caller's own
 * re-render depends on this resolving.
 */
export async function applyLocale(locale: Locale): Promise<void> {
  const turn = ++applying;
  const missions = await missionCatalog();
  let text: Map<string, MissionText> | null = null;
  if (locale !== 'en') {
    try {
      text = await fetchOverlay(locale);
    } catch {
      // Offline, not built yet, or served by something that does not have it.
      // Every string falls back to English below.
      text = null;
    }
  }
  // Overtaken while the overlay was on its way. Two quick clicks are Arabic
  // then English, and English needs no fetch, so it finishes first; the
  // Arabic landing after it would write Arabic under an English interface,
  // and nothing would put it right until the next switch. The latest call is
  // the one the learner asked for, so it is the only one that writes.
  if (turn !== applying) return;
  // Per mission, because one of them must not be able to take the language
  // down with it.
  //
  // This loop used to be bare, and a single throw in it did far more damage
  // than the level it came from. `applyLocale` rejects, so the caller's
  // `.then(refreshMissions)` never runs — and `refreshMissions` is the only
  // thing that tells React the mission objects it is holding copies of have
  // changed. The whole interface stays in English, including the levels the
  // loop had already translated before it fell over, and nothing anywhere
  // says why. That is how `missionText` throwing on a locked stub
  // (packages/engine/src/i18n.ts) shipped an English course to every Arabic
  // reader without a single visible error.
  //
  // The same judgement the overlay fetch above makes, made one level down: a
  // translation is decoration over a catalogue that already works, and the
  // cost of a failure has to stay proportional to it. One untranslated level
  // is a defect; fifty are a broken feature.
  const failed: string[] = [];
  for (const m of missions.values()) {
    try {
      localise(m, locale, text);
    } catch (err) {
      failed.push(`${m.id}: ${(err as Error).message}`);
    }
  }
  // Not silent. Swallowing the throw is what keeps the course usable; leaving
  // no trace of it is what let this one live in production, so the one thing
  // that must survive the catch is the sentence naming what could not be
  // written and where.
  if (failed.length > 0) console.warn(`i18n: ${failed.length} level(s) kept their English — ${failed.join('; ')}`);
  applied = locale;
  // For whatever holds a copy of a level's words rather than reading the
  // catalogue — the open level's session, above all (lib/locale.ts).
  levelsLocalised(locale);
}

/** What `unlockPaid` found: how the request was answered, and what it changed. */
export type PaidResult =
  | { state: 'unlocked'; added: number }
  | { state: 'anonymous' }
  | { state: 'unowned' }
  | { state: 'unavailable' };

/**
 * The efficiency benchmark's tasks (docs/efficiency-bench.md), for a
 * signed-in tab.
 *
 * Not levels: the course catalogue never names them and `api.listMissions`
 * leaves them out, so the map never draws one. They join the runtime's `Map`
 * all the same, because that is what the session manager seeds, opens and
 * exports a mission from by id. Each is graded on the server like a
 * checkpoint (`graded: 'server'`) and carries the titles of its visible
 * objectives and nothing behind them. Fetched once; a refusal (signed out)
 * is not remembered, so signing in asks again.
 */
let benchLoad: Promise<Mission[]> | null = null;
export function benchMissions(): Promise<Mission[]> {
  benchLoad ??= (async () => {
    const missions = await missionCatalog();
    const res = await fetch(BENCH_MISSIONS_URL, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw Object.assign(new Error(`${BENCH_MISSIONS_URL} answered ${res.status}`), { status: res.status });
    const raw = (await res.json()) as { schema?: unknown; missions?: unknown } | null;
    if (!raw || raw.schema !== MISSIONS_SCHEMA || !Array.isArray(raw.missions)) {
      throw new Error(`${BENCH_MISSIONS_URL} is not a schema ${MISSIONS_SCHEMA} mission file`);
    }
    const out: Mission[] = [];
    for (const m of raw.missions as Mission[]) {
      // Server-graded, with a list of what it asks for, and never a course id:
      // a bench mission may not stand in for a level the map already has.
      if (typeof m?.id !== 'string' || !m.id.startsWith('bench-') || m.track !== 'bench' || m.graded !== 'server' ||
          !Array.isArray(m.objectives) || m.objectives.length === 0 || !Array.isArray(m.requires)) {
        throw new Error(`${BENCH_MISSIONS_URL} contains something that is not a bench mission`);
      }
      const held = missions.get(m.id);
      if (held && held.track !== 'bench') continue;
      if (!held) missions.set(m.id, m);
      out.push(missions.get(m.id)!);
    }
    return out;
  })().catch((error: unknown) => {
    benchLoad = null;
    throw error;
  });
  return benchLoad;
}

/**
 * Ask the Worker for the paid half and fold it into the catalogue.
 *
 * The catalogue is one `Map`, and `bootRuntime` hands that same `Map` to the
 * `SessionManager` rather than a copy — so replacing a stub here replaces it
 * for the grader and the seeder too, and a level bought in this tab becomes
 * playable in this tab. That is the reason this mutates in place instead of
 * rebuilding: a purchase should not need a reload.
 *
 * Not `once()`. It is called when the catalogue loads, again when somebody
 * signs in, and again after a purchase, and each of those is a different
 * answer to the same question. Calling it when nothing has changed costs one
 * conditional request and replaces thirty entries with themselves.
 *
 * A refusal is a state, not a failure: 401 means nobody is signed in and 402
 * means this account has not bought the course, and both are ordinary things
 * for the page to be told. Only a broken answer throws.
 */
export async function unlockPaid(): Promise<PaidResult> {
  const missions = await missionCatalog();
  let res: Response;
  try {
    res = await fetch(PAID_MISSIONS_URL, { credentials: 'same-origin', cache: 'no-store' });
  } catch {
    // Offline, or no Worker in front of this page at all (a plain `vite
    // preview` of the built app). The free half still works; say so rather
    // than failing the boot.
    return { state: 'unavailable' };
  }
  if (res.status === 401) return { state: 'anonymous' };
  if (res.status === 402) return { state: 'unowned' };
  if (!res.ok) return { state: 'unavailable' };

  const raw = (await res.json()) as { schema?: unknown; missions?: unknown } | null;
  if (!raw || typeof raw !== 'object' || raw.schema !== MISSIONS_SCHEMA) {
    throw new Error(`${PAID_MISSIONS_URL} is not a schema ${MISSIONS_SCHEMA} mission file`);
  }
  if (!Array.isArray(raw.missions)) throw new Error(`${PAID_MISSIONS_URL} carried no levels`);

  let added = 0;
  for (const m of raw.missions as Mission[]) {
    // The Worker is trusted to send missions, not to send anything. The
    // length check is the point: a level arriving with an empty objective
    // list would replace a stub that at least knew it was a stub, and would
    // then grade vacuously — `every` over nothing is true — handing out a
    // free clear on a level somebody paid for.
    if (typeof m?.id !== 'string' || !Number.isInteger(m?.level) || !Array.isArray(m?.objectives) || m.objectives.length === 0) {
      throw new Error(`${PAID_MISSIONS_URL} contains a level that is not a mission`);
    }
    if (m.locked) throw new Error(`${PAID_MISSIONS_URL} contains a level that is still locked`);
    // Only ever replaces a level the free bundle already named, so a Worker
    // and an app bundle from different builds cannot between them invent a
    // level the map has no card for.
    if (!missions.has(m.id)) continue;
    missions.set(m.id, m);
    added++;
  }

  /**
   * A level that has just arrived is in English, whatever language the page
   * is in: the Worker sent the source, and the overlay that was applied at
   * startup ran before this mission existed. So the paid half is translated
   * here, on the way in.
   *
   * The overlay for the paid half comes from behind the same entitlement as
   * the paid half itself — a translated brief is still the brief. It is
   * fetched after the levels rather than beside them so that a failure to get
   * it costs the learner Arabic and never costs them the levels.
   */
  if (applied !== 'en' && added > 0) {
    const url = localePaidUrl(applied);
    let text: Map<string, MissionText> | null = null;
    try {
      // `same-origin`, not `omit`: this route is behind the same session
      // check the levels themselves are, so it needs the same cookie.
      const translated = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (translated.ok) text = readOverlay(await translated.json(), url);
    } catch {
      text = null;
    }
    for (const m of raw.missions as Mission[]) {
      const held = missions.get(m.id);
      if (held) localise(held, applied, text);
    }
  }

  return { state: 'unlocked', added };
}

/**
 * Take the paid half back out of the catalogue.
 *
 * The exact inverse of `unlockPaid`, and it exists because the unlock was
 * never undone. The `Map` is the tab's one copy of the course; folding levels
 * into it is what makes a purchase land without a reload, and nothing put
 * them back when the reason for the fold went away. Signing out is that
 * moment: `usePaid` re-asks the Worker, is told 401, and up to now recorded
 * `anonymous` beside a catalogue that still held the whole course.
 *
 * What that cost was the offer. The block at the foot of the ledger draws
 * itself off `paidSpan`, which asks the catalogue what is locked rather than
 * asking the account what it owns — deliberately, so that a build with no
 * paid half never advertises one (lib/entitlement.ts). With nothing locked
 * the answer is "there is nothing to sell", and the price, the buy button and
 * the invite-code link all left the page for somebody who had just signed out
 * and could no longer play a single one of the levels they were now not being
 * offered. The same staleness made every paid row on the map claim it was
 * waiting on progress rather than on payment, and `ownedCount` count every
 * level in the course as owned by a browser that owned none of them.
 *
 * Only ever called for an answer the Worker actually gave — 401 or 402. An
 * unreachable Worker is not told to take anything away: a learner who owns
 * the course and loses their connection mid-level keeps it, which is the same
 * judgement `PaidState.unavailable` makes everywhere else.
 *
 * Returns how many levels were put back, so the caller can skip the re-read
 * when the answer is none. On the first load it is always none: the catalogue
 * arrives locked and this is a walk over a map comparing objects to
 * themselves.
 */
export async function relockPaid(): Promise<number> {
  const missions = await missionCatalog();
  const restored: Mission[] = [];
  for (const [id, stub] of stubs) {
    // Already the stub. Not `!missions.get(id)?.locked`, because identity is
    // the fact worth checking: the entry either is the object this catalogue
    // was built with or it is something that replaced it.
    if (missions.get(id) === stub) continue;
    missions.set(id, stub);
    restored.push(stub);
  }
  if (restored.length === 0) return 0;

  /*
   * A stub that has been out of the catalogue has missed every language
   * switch made while it was out — `applyLocale` walks the `Map`, and these
   * were not in it. So they are put into the language the page is actually in
   * on the way back, the way `unlockPaid` does it on the way in. Usually free:
   * the overlay for a language already applied is in `overlays`, and a stub
   * carries a title and a module and nothing else (packages/engine, `lockedText`).
   */
  let text: Map<string, MissionText> | null = null;
  if (applied !== 'en') {
    try {
      text = await fetchOverlay(applied);
    } catch {
      // English on the cards for these two strings, which is the fallback
      // every other translation failure in this file takes.
      text = null;
    }
  }
  for (const m of restored) localise(m, applied, text);

  return restored.length;
}

let current: Runtime | null = null;

/**
 * Boot the VM and build the runtime over it. Idempotent, so the boot screen
 * calls it to start the download and api.ts calls it to wait for the same
 * one; whoever gets there first wins.
 */
export const bootRuntime = once(async (): Promise<Runtime> => {
  const [missions, manifest] = await Promise.all([missionCatalog(), fetchManifest(MANIFEST_URL)]);
  const vm = await bootVm({ manifest, assetBase: ASSET_BASE, wasmUrl: WASM_URL, onProgress: report });
/**
 * This learner's salt for a level, fetched once and remembered.
 *
 * Only the salted levels ask, so an ordinary level never waits on the network
 * to open. The answer is cached because it does not change inside a session —
 * the generation only moves when somebody asks for a fresh attempt, which is
 * a reload either way — and a *failure* is deliberately not cached, so a
 * learner who signs in after being refused gets a real answer on the retry.
 *
 * It throws rather than returning null. A checkpoint seeded from the shared
 * bytes is twenty-five minutes of work the server can only refuse, and a
 * refusal now is cheaper than a refusal then.
 */
let saltCache: Promise<Record<string, string>> | null = null;

async function saltForMission(missionId: string): Promise<string | null> {
  if (!saltedMission(missionId)) return null;
  const pending = saltCache ?? (saltCache = seedSalts().catch((error: unknown) => {
    saltCache = null;
    throw error;
  }));
  const salt = (await pending)[missionId];
  if (!salt) throw new Error(`this level needs an account: sign in to open "${missionId}"`);
  return salt;
}

  const keeper = new WorkKeeper(saltForMission);
  current = new BrowserRuntime(vm, missions,
    new SessionManager(new BrowserDriver(vm), missions,
      new TarballSeeder(manifest.levels, saltForMission, reportLevel, keeper)),
    keeper);
  return current;
});

/**
 * The runtime if it is already up, without waiting for a boot. For callers
 * that must answer now and have something sensible to say when it is not:
 * the health line, and anything reporting on a session that may not exist.
 */
export function activeRuntime(): Runtime | null {
  return current;
}
